import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, KeyRound, ShieldAlert, Users } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function StatusBadge({ status }) {
  const tone = {
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    suspended: 'border-amber-300 bg-amber-50 text-amber-800',
    revoked: 'border-red-300 bg-red-50 text-red-700',
    draft: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'draft').replaceAll('_', ' ')}</span>;
}

function PolicyBadge({ policy }) {
  const status = policy?.status || 'not_ready';
  const tone = {
    internal_authentication_ready: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    internal_evidence_ready: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    internal_foundation: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    not_ready: 'border-amber-300 bg-amber-50 text-amber-800',
    blocked: 'border-red-300 bg-red-50 text-red-700',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  const label = {
    internal_authentication_ready: 'Internal authentication ready',
    internal_evidence_ready: 'Internal evidence ready',
    internal_foundation: 'Internal foundation',
    not_ready: 'Not ready',
    blocked: 'Blocked',
  }[status] || String(status).replaceAll('_', ' ');
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{label}</span>;
}

function SummaryCard({ title, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{title}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

export default function DigitalKeyAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activationLinks, setActivationLinks] = useState({});
  const [workingAccountId, setWorkingAccountId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/idem-identities/admin/identities');
      const identities = data.identities || [];
      const linkedRows = await Promise.all(identities.map(async (identity) => {
        const identityRef = identity.public_id || identity.id;
        try {
          const { data: accountData } = await api.get(`/digital-key-accounts/admin/identities/${identity.id}/digital-key-account`);
          const account = accountData.account || null;
          if (!account) {
            return {
              identity,
              identityRef,
              account: null,
              policy: null,
              recentRequests: [],
              lastRequest: null,
              rowIssue: null,
            };
          }

          const [policyResult, requestsResult] = await Promise.allSettled([
            api.get(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/assurance-policy`),
            api.get(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/login-requests?limit=5`),
          ]);
          const policy = policyResult.status === 'fulfilled' ? policyResult.value.data.policy || null : null;
          const recentRequests = requestsResult.status === 'fulfilled' ? requestsResult.value.data.requests || [] : [];
          return {
            identity,
            identityRef,
            account,
            policy,
            recentRequests,
            lastRequest: recentRequests[0] || null,
            rowIssue: null,
          };
        } catch (requestError) {
          return {
            identity,
            identityRef,
            account: null,
            policy: null,
            recentRequests: [],
            lastRequest: null,
            rowIssue: requestError?.response?.status === 404
              ? null
              : 'Some linked digital key data could not be loaded.',
          };
        }
      }));
      setRows(linkedRows);
    } catch (requestError) {
      setError(requestError?.response?.status === 403
        ? 'This internal page is not available for this account.'
        : 'Digital Key Accounts could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generateActivationLink = async (account) => {
    setWorkingAccountId(account.id);
    setError('');
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/activation-links`);
      const activationUrl = data.activation_url || data.activation_link?.activation_url || '';
      if (activationUrl) {
        setActivationLinks((current) => ({ ...current, [account.id]: activationUrl }));
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(`${window.location.origin}${activationUrl}`);
        }
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The lab activation link could not be generated.');
    } finally {
      setWorkingAccountId('');
    }
  };

  useEffect(() => { load(); }, [load]);

  const linkedAccounts = useMemo(() => rows.filter((row) => row.account), [rows]);
  const loadIssues = useMemo(() => rows.filter((row) => row.rowIssue).length, [rows]);
  const summary = useMemo(() => ({
    linked: linkedAccounts.length,
    active: linkedAccounts.filter((row) => row.account.status === 'active').length,
    notReady: linkedAccounts.filter((row) => row.policy?.status === 'not_ready' || row.policy?.can_authenticate === false).length,
    unlinked: rows.filter((row) => !row.account && !row.rowIssue).length,
  }), [linkedAccounts, rows]);

  return (
    <div data-testid="digital-key-accounts-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Digital Key Accounts" showDashboard={false} />

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Digital Key Accounts</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">Safe internal overview of idem identity to digital key account linking, assurance policy state, and recent login-request activity. This is internal preparation only.</p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-medium">Internal-only account overview</p>
            <p className="mt-1">This page does not expose raw evidence, documents, selfies, RRN/BIS, OCR/MRZ raw data, tokens, API keys, or webhook secrets. It is not a public login or production OIDC surface.</p>
          </div>
        </div>
      </section>

      {loadIssues > 0 && (
        <section className="mt-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Some linked rows could not be loaded</p>
          <p className="mt-1">The page kept rendering the identities and accounts that were available. Refreshing or opening an individual identity may show the missing row once the backend lookup is healthy again.</p>
        </section>
      )}

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard title="Linked accounts" value={summary.linked} detail="Identity-linked digital key accounts." />
        <SummaryCard title="Active accounts" value={summary.active} detail="Accounts currently eligible for internal lab flows." />
        <SummaryCard title="Needs validation" value={summary.notReady} detail="Accounts whose policy is not yet authentication-ready." />
        <SummaryCard title="Unlinked identities" value={summary.unlinked} detail="Identity records without a digital key account yet." />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Create or link accounts</h2>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Create digital key accounts from an approved identity detail page. This keeps the linking flow tied to the source verification and the internal identity record.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/internal/digital-key-demo" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open demo runner</Link>
          <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open identities</Link>
          <Link to="/internal/digital-key-architecture" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open architecture</Link>
          <Link to="/internal/oidc-lab" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open OIDC lab</Link>
        </div>
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Overview table</h2>
            <p className="mt-1 text-sm text-zinc-600">Each row shows safe metadata only. Open the identity detail page to manage the linked account.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open identities</Link>
            <Link to="/internal/digital-key-architecture" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open architecture</Link>
          </div>
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-zinc-500">Loading digital key accounts…</p>
        ) : error ? (
          <p className="mt-4 text-sm text-red-700">{error}</p>
        ) : linkedAccounts.length === 0 ? (
          <div className="mt-4 border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
            <p className="font-medium text-zinc-800">{loadIssues > 0 ? 'Some internal digital key rows could not be loaded.' : 'No digital key account linked yet.'}</p>
            <p className="mt-1">{loadIssues > 0 ? 'The page kept safe fallback rows where possible. Open an individual identity or refresh after the backend lookup is healthy.' : 'Open an identity record to create or link a digital key account from the identity detail page.'}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/internal/digital-key-demo" className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open demo runner
              </Link>
              <Link to="/internal/identities" className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open identities
              </Link>
              <Link to="/internal/oidc-lab" className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open OIDC lab
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[1100px] w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Identity</th>
                  <th className="py-2 pr-4 font-medium">Account ID</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Assurance policy</th>
                  <th className="py-2 pr-4 font-medium">Last login request</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 pr-4 font-medium">Updated</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {linkedAccounts.map((row) => (
                  <tr key={row.account.id} className="border-b border-zinc-100 last:border-b-0 align-top">
                    <td className="py-3 pr-4">
                      <Link to={`/internal/identities/${row.identityRef || row.identity.id}`} className="font-medium text-zinc-800 hover:text-black hover:underline">{row.identity.public_id}</Link>
                      <div className="mt-1 font-mono text-[10px] text-zinc-500">{row.identity.id}</div>
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-zinc-700">{row.account.id}</td>
                    <td className="py-3 pr-4">
                      <StatusBadge status={row.account.status} />
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-600">
                      <PolicyBadge policy={row.policy} />
                      <div className="mt-1">
                        {row.policy ? (
                          <span>{row.policy.can_authenticate ? 'Can authenticate' : 'Blocked'} · max {String(row.policy.max_internal_assurance || 'not_ready').replaceAll('_', ' ')}</span>
                        ) : (
                          'Policy not available'
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-600">
                      {row.lastRequest ? (
                        <>
                          <StatusBadge status={row.lastRequest.status} />
                          <div className="mt-1">
                            <div>{formatDate(row.lastRequest.updated_at || row.lastRequest.created_at)}</div>
                            <div className="mt-1 text-zinc-500">Most recent of {row.recentRequests.length} shown request(s)</div>
                          </div>
                        </>
                      ) : (
                        'No login requests yet'
                      )}
                    </td>
                    <td className="py-3 pr-4 text-xs text-zinc-500">{formatDate(row.account.created_at)}</td>
                    <td className="py-3 pr-4 text-xs text-zinc-500">{formatDate(row.account.updated_at)}</td>
                    <td className="py-3 text-xs">
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/internal/identities/${row.identityRef || row.identity.id}`} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Identity
                        </Link>
                        <Link to={`/internal/oidc-lab?identity_id=${encodeURIComponent(row.identityRef || row.identity.id)}&account_id=${encodeURIComponent(row.account.id)}`} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Start lab login
                        </Link>
                        <Link to="/internal/oidc-lab" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                          OIDC lab
                        </Link>
                        <Link to="/internal/authenticator-preview" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
                          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Authenticator
                        </Link>
                        <button
                          type="button"
                          onClick={() => generateActivationLink(row.account)}
                          disabled={workingAccountId === row.account.id}
                          className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400 disabled:opacity-50"
                        >
                          <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Generate lab activation link
                        </button>
                      </div>
                      {activationLinks[row.account.id] && (
                        <div className="mt-2 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                          <div className="font-mono break-all">{activationLinks[row.account.id]}</div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
